import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { AuthUser, LoginResponse } from '../interfaces/auth.interface';
import { AppURL } from '../app.url';
import { AUTH_KEYS, clearSession, saveSession } from './auth.storage';

// setTimeout รับค่าได้สูงสุด ~24.8 วัน
const MAX_TIMER_MS = 2 ** 31 - 1;

/** login ด้วยบัญชี Active Directory ผ่าน API (ออก JWT) — เก็บ token ไว้ใน localStorage (หรือได้มาจาก SSO ดู sso.ts) */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private _token: string | null = null;
  private _expire: number = 0;
  private _expiryTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private _http: HttpClient,
    private _router: Router
  ) {
    this.loadSession();
    this.scheduleExpiry();
    // login/logout จาก tab อื่น → ทำตามใน tab นี้ด้วย
    window.addEventListener('storage', e => this.onStorage(e));
  }

  /** token ที่ยังไม่หมดอายุ (null = ยังไม่ login / หมดอายุแล้ว) */
  get access_token(): string | null {
    return this._token && this._expire > Date.now() ? this._token : null;
  }

  get isLoggedIn(): boolean {
    return !!this.access_token;
  }

  get user(): AuthUser | null {
    try { return JSON.parse(localStorage.getItem(AUTH_KEYS.user) || 'null'); }
    catch { return null; }
  }

  login(username: string, password: string): Observable<AuthUser> {
    return this._http.post<LoginResponse>('auth/login', { username, password }).pipe(
      tap(res => {
        this._token = res.data.token;
        this._expire = Date.parse(res.data.expires_at);
        saveSession(res.data);
        this.scheduleExpiry();
      }),
      map(res => res.data.user)
    );
  }

  /** ออกจากระบบแล้วไปหน้า login — keepReturnUrl = กลับมาหน้าเดิมหลัง login ใหม่ (กรณี token หมดอายุ) */
  logout(keepReturnUrl: boolean = false) {
    this._token = null;
    this._expire = 0;
    clearTimeout(this._expiryTimer);
    clearSession();

    if (this.onLoginPage) return;
    const queryParams = keepReturnUrl ? { redirect: this._router.url } : {};
    this._router.navigate(['/', AppURL.login], { queryParams });
  }

  private get onLoginPage(): boolean {
    return this._router.url.startsWith(`/${AppURL.login}`);
  }

  private loadSession() {
    this._token = localStorage.getItem(AUTH_KEYS.token);
    this._expire = Date.parse(localStorage.getItem(AUTH_KEYS.expire) || '') || 0;
  }

  /** เปิดหน้าค้างไว้จน token หมดอายุ → เด้งไปหน้า login เอง แล้วกลับมาหน้าเดิมหลัง login ใหม่ */
  private scheduleExpiry() {
    clearTimeout(this._expiryTimer);
    if (!this.isLoggedIn) return;
    const ms = Math.min(this._expire - Date.now() + 1000, MAX_TIMER_MS);
    this._expiryTimer = setTimeout(() => this.isLoggedIn ? this.scheduleExpiry() : this.logout(true), ms);
  }

  private onStorage(e: StorageEvent) {
    // key = null คือ localStorage.clear()
    if (e.key !== null && e.key !== AUTH_KEYS.token && e.key !== AUTH_KEYS.expire) return;
    const wasLoggedIn = this.isLoggedIn;
    this.loadSession();
    if (this.isLoggedIn) {
      this.scheduleExpiry();
      if (this.onLoginPage) this._router.navigateByUrl('/');
    } else if (wasLoggedIn) {
      this.logout(true);
    }
  }
}
